import React, { useState } from "react";
import {
  Menu,
  X,
  ArrowRight,
  Target,
  BarChart3,
  CheckCircle2,
} from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/home" className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "Case Studies"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">
            Log In
          </a>
        </div>

        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-bold ${
                  link.name === "Case Studies" ? "text-blue-600" : "text-slate-900"
                }`}
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

const CaseStudyCard = () => (
  <section className="pt-36 pb-16 px-6">
    <div className="max-w-5xl mx-auto">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
        Featured Case Study
      </div>

      <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 mb-5">
        10% to 40% from 3-Point Range in 8 Weeks
      </h1>

      <p className="text-slate-600 text-lg max-w-4xl mb-10">
        A basketball team improved from <strong>10% 3PT shooting</strong> to <strong>40%</strong> in
        two months by running structured practices <strong>2x per week</strong> and building a
        comprehensive SWOT analysis for each player.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Start</p>
          <p className="text-3xl font-black text-slate-900">10%</p>
          <p className="text-sm text-slate-500 mt-1">Team 3PT Percentage</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Finish</p>
          <p className="text-3xl font-black text-green-600">40%</p>
          <p className="text-sm text-slate-500 mt-1">Team 3PT Percentage</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Cadence</p>
          <p className="text-3xl font-black text-slate-900">2x / week</p>
          <p className="text-sm text-slate-500 mt-1">Practice Frequency</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Target size={20} className="text-blue-600" />
            What Changed
          </h2>
          <ul className="space-y-3 text-slate-700">
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Individual SWOTs identified each athlete&apos;s highest-impact shooting gaps.
            </li>
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Practice reps shifted from generic volume to role-specific shot profiles.
            </li>
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Micro-point priorities gave coaches clear weekly execution goals.
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 p-6 bg-slate-50">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600" />
            Why It Worked
          </h2>
          <ul className="space-y-3 text-slate-700">
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Every athlete had a defined development track and success criteria.
            </li>
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Coaches could monitor completion, quality, and consistency in one place.
            </li>
            <li className="flex gap-2">
              <CheckCircle2 size={18} className="text-blue-600 mt-0.5 shrink-0" />
              Feedback loops happened weekly, not just after games.
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export default function CaseStudiesPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />
      <CaseStudyCard />
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto rounded-2xl border border-slate-200 p-8 bg-slate-50">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-3">Want the same result?</h2>
          <p className="text-slate-600 mb-6">
            Apply this same SWOT-first system to your roster and build role-specific development
            plans for every athlete.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="/home/about"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white border border-slate-300 text-slate-800 font-semibold hover:bg-slate-100 transition-colors"
            >
              About Our Mission
            </a>
            <a
              href="/create-account"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
            >
              Build Your Plan <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
